import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, api, BRAND } from "@/lib/api";
import { ExternalLink } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { Logo } from "@/components/Logo";
import { AUTH_TITLES, AuthFields, AuthFeedback, ModeToggle } from "@/components/AuthParts";

const useAuthForm = () => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const set = (k) => (e) => { const v = e.target.value; setForm((p) => ({ ...p, [k]: v })); };
  const switchMode = (m) => { setMode(m); setError(""); setSent(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setSent(data.message);
        return;
      }
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(`Welcome, ${u.name}`);
      navigate(params.get("next") || "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return { mode, form, error, sent, busy, set, switchMode, submit };
};

const submitLabel = (mode, busy) => {
  if (busy) return "Please wait…";
  return mode === "forgot" ? "Email me a reset link" : AUTH_TITLES[mode];
};

export default function Auth() {
  const { mode, form, error, sent, busy, set, switchMode, submit } = useAuthForm();
  return (
    <div className="grid-bg flex min-h-screen flex-col bg-slate-950 px-6 py-6">
      <div className="mx-auto flex w-full max-w-md items-center justify-between">
        <BackButton />
        <div className="flex items-center gap-4"><a href={BRAND.mainSite} data-testid="auth-main-site-link" className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /> FLS main website</a><Logo testId="auth-logo-link" /></div>
      </div>
      <div className="mx-auto mt-16 w-full max-w-md fade-up">
        <div className="panel p-8">
          <h1 className="font-heading text-3xl font-bold text-white">{AUTH_TITLES[mode]}</h1>
          <p className="mt-1 text-sm text-slate-400">{mode === "forgot" ? "Enter your email and we'll send you a link that works for 1 hour." : "Access tickets, community and the marketplace."}</p>
          <div className="laser-line my-6 w-16" />
          {sent ? (
            <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-300" data-testid="forgot-sent-message">{sent}</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
              <AuthFields mode={mode} form={form} set={set} onForgot={() => switchMode("forgot")} />
              <AuthFeedback error={error} />
              <button type="submit" disabled={busy} className="btn-primary w-full py-3" data-testid="auth-submit-button">
                {submitLabel(mode, busy)}
              </button>
            </form>
          )}
          <ModeToggle mode={mode} onSwitch={switchMode} />
        </div>
      </div>
    </div>
  );
}
